<!-- app/javascript/dashboard/routes/dashboard/bulkmessage/components/modals/EmptyValuesValidationModal.vue -->

<script setup>
import { computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  emptyFieldsData: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close', 'continue', 'go-back']);

const totalRecipients = computed(() => {
  return props.emptyFieldsData.totalRecipients || 0;
});

const emptyFields = computed(() => {
  return props.emptyFieldsData.emptyFields || [];
});

const recipientsWithEmpty = computed(() => {
  return props.emptyFieldsData.recipientsWithEmpty || 0;
});

const handleGoBack = () => {
  emit('go-back');
};

const handleContinue = () => {
  emit('continue');
};

const handleClose = () => {
  emit('close');
};
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black75 dark:bg-n-alpha-black75 backdrop-blur-sm"
  >
    <div
      class="relative w-full max-w-2xl max-h-screen overflow-hidden bg-n-solid-1 dark:bg-n-solid-1 border border-n-slate-6 dark:border-n-slate-6 shadow-2xl rounded-xl flex flex-col animate-in fade-in-0 zoom-in-95 duration-300"
    >
      <!-- Header -->
      <div
        class="flex justify-between items-center px-5 py-4 border-b border-n-slate-6 dark:border-n-slate-6 bg-n-slate-2 dark:bg-n-slate-2 rounded-t-xl"
      >
        <div class="flex items-center gap-3">
          <div
            class="w-10 h-10 bg-gradient-to-br from-n-amber-9 to-n-amber-10 dark:from-n-amber-9 dark:to-n-amber-10 rounded-lg flex items-center justify-center shadow-md"
          >
            <i class="i-lucide-alert-triangle text-white text-base" />
          </div>
          <div>
            <h3 class="text-xl font-bold text-n-slate-12 dark:text-n-slate-12">
              Valores em Branco Detectados
            </h3>
            <p class="text-sm text-n-slate-11 dark:text-n-slate-11 font-medium">
              Alguns contatos possuem campos vazios
            </p>
          </div>
        </div>
        <button
          class="text-n-slate-11 dark:text-n-slate-11 p-2 hover:bg-n-slate-3 dark:hover:bg-n-slate-3 hover:text-n-slate-12 dark:hover:text-n-slate-12 rounded-lg transition-all duration-200"
          @click="handleClose"
        >
          <i class="i-lucide-x text-lg" />
        </button>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto px-5 py-4">
        <div
          class="bg-gradient-to-r from-n-amber-2 to-n-amber-3 dark:from-n-amber-2 dark:to-n-amber-3 border border-n-amber-6 dark:border-n-amber-6 rounded-lg p-4 mb-4"
        >
          <div class="flex items-start gap-3">
            <div
              class="w-8 h-8 bg-gradient-to-br from-n-amber-9 to-n-amber-10 dark:from-n-amber-9 dark:to-n-amber-10 rounded-lg flex items-center justify-center shadow-sm"
            >
              <i class="i-lucide-info text-white text-sm" />
            </div>
            <div>
              <h4
                class="text-base font-bold text-n-amber-12 dark:text-n-amber-12 mb-2"
              >
                Resumo da Validação
              </h4>
              <div class="space-y-1 text-sm text-n-amber-11 dark:text-n-amber-11">
                <p>
                  <strong>{{ recipientsWithEmpty }}</strong> de
                  <strong>{{ totalRecipients }}</strong> contatos possuem campos
                  vazios
                </p>
                <p>
                  <strong>Campos afetados:</strong>
                  {{ emptyFields.join(', ') }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div
          class="bg-n-solid-1 dark:bg-n-solid-1 border border-n-slate-6 dark:border-n-slate-6 rounded-lg p-4"
        >
          <h4
            class="text-base font-bold text-n-slate-12 dark:text-n-slate-12 mb-3"
          >
            O que isso significa?
          </h4>
          <div class="space-y-3 text-sm text-n-slate-11 dark:text-n-slate-11">
            <div class="flex items-start gap-3">
              <div
                class="w-6 h-6 bg-n-iris-3 dark:bg-n-iris-3 rounded flex items-center justify-center flex-shrink-0 mt-0.5"
              >
                <i class="i-lucide-message-square text-n-iris-11 text-xs" />
              </div>
              <p>
                Quando você usar variáveis na mensagem (como
                <code class="bg-n-slate-2 px-1 rounded">{{NOME}}</code> ou
                <code class="bg-n-slate-2 px-1 rounded">{{EMAIL}}</code>), os
                contatos com campos vazios receberão mensagens com espaços em
                branco no lugar das variáveis.
              </p>
            </div>
            <div class="flex items-start gap-3">
              <div
                class="w-6 h-6 bg-n-teal-3 dark:bg-n-teal-3 rounded flex items-center justify-center flex-shrink-0 mt-0.5"
              >
                <i class="i-lucide-check-circle text-n-teal-11 text-xs" />
              </div>
              <p>
                Você pode prosseguir mesmo assim ou voltar para revisar e
                completar os dados dos contatos.
              </p>
            </div>
          </div>
        </div>

        <div
          class="bg-gradient-to-r from-n-iris-2 to-n-iris-3 dark:from-n-iris-2 dark:to-n-iris-3 border border-n-iris-6 dark:border-n-iris-6 rounded-lg p-4 mt-4"
        >
          <h4
            class="text-base font-bold text-n-iris-12 dark:text-n-iris-12 mb-2"
          >
            Exemplo de como ficaria a mensagem:
          </h4>
          <div
            class="bg-n-solid-1 dark:bg-n-solid-1 border border-n-slate-6 dark:border-n-slate-6 rounded-lg p-3"
          >
            <p class="text-sm text-n-slate-12 dark:text-n-slate-12 mb-2">
              <strong>Mensagem original:</strong>
            </p>
            <p
              class="text-sm text-n-slate-11 dark:text-n-slate-11 font-mono bg-n-slate-2 dark:bg-n-slate-2 p-2 rounded mb-3"
            >
              Olá {{NOME}}, seu email {{EMAIL}} foi cadastrado com sucesso!
            </p>
            <p class="text-sm text-n-slate-12 dark:text-n-slate-12 mb-2">
              <strong>Como ficaria com campo vazio:</strong>
            </p>
            <p
              class="text-sm text-n-slate-11 dark:text-n-slate-11 font-mono bg-n-amber-2 dark:bg-n-amber-2 p-2 rounded"
            >
              Olá João Silva, seu email foi cadastrado com sucesso!
            </p>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex justify-between items-center px-5 py-4 border-t border-n-slate-6 dark:border-n-slate-6 bg-n-slate-2 dark:bg-n-slate-2 rounded-b-xl"
      >
        <Button
          label="← Voltar"
          variant="outline"
          color="slate"
          class="transition-all duration-200 hover:scale-105"
          @click="handleGoBack"
        />

        <div class="flex gap-3">
          <Button
            label="Prosseguir Mesmo Assim"
            color="amber"
            class="transition-all duration-200 hover:scale-105"
            @click="handleContinue"
          />
        </div>
      </div>
    </div>
  </div>
</template>